import React, { useMemo, useState } from 'react';
import { subjectNamesFromStandards } from '../../utils/evaluationPlanDocTitle';
import type {
  EvaluationLevelDisplayLabels,
  EvaluationLevelDisplayLabelsByStepCount,
  EvaluationLevelStepCount,
  EvaluationPlanDraft,
  EvaluationPlanRowScope,
  EvaluationStandardDraft,
} from '../../types/evaluationPlan';
import {
  DEFAULT_LEVEL_STEP_COUNT,
  EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS,
  clampLevelStepCount,
  comparePlansByLinkedStandards,
  compareStandardsByCode,
  formatPlanListSummary,
  formatStandardSummary,
  getEvaluationPlanAttachment,
  getLevelDisplayLabelsForStep,
  hasEvaluationPlanSlotAttachment,
  planMatchesFilter,
  type EvaluationPlanAttachmentSlot,
} from '../../types/evaluationPlan';
import { isShareLinkAttachment, isSheetCanvasAttachment, isWebDocumentAttachment } from '../../utils/evaluationPlanAttachment';
import EvaluationPlanAttachmentPreviewModal from './EvaluationPlanAttachmentPreviewModal';
import EvaluationPlanDeleteHoldButton from './EvaluationPlanDeleteHoldButton';
import EvaluationPlanShareLinkActionPopover from './EvaluationPlanShareLinkActionPopover';
import EvaluationPlanWebDocumentViewerModal from './EvaluationPlanWebDocumentViewerModal';
import EvaluationPlanSheetCanvasViewerModal from './EvaluationPlanSheetCanvasViewerModal';

function getPlanLinkedStandards(
  plan: EvaluationPlanDraft,
  standards: EvaluationStandardDraft[]
): EvaluationStandardDraft[] {
  const byId = new Map(standards.map((row) => [row.id, row]));
  return (plan.standard_ids ?? [])
    .map((id) => byId.get(id))
    .filter((row): row is EvaluationStandardDraft => !!row)
    .sort(compareStandardsByCode);
}

const EvaluationPlanListPanel: React.FC<{
  standards: EvaluationStandardDraft[];
  plans: EvaluationPlanDraft[];
  allPlans: EvaluationPlanDraft[];
  filterQuery: string;
  filterStandardId: string | null;
  readOnly?: boolean;
  allowAddPlan?: boolean;
  editablePlanIds?: Set<string>;
  planScopeById?: Record<string, EvaluationPlanRowScope>;
  /** 기본 단계(폴백). 문서 미리보기는 각 계획의 level_step_count를 우선 사용 */
  levelStepCount?: EvaluationLevelStepCount;
  levelDisplayLabels?: EvaluationLevelDisplayLabels | null;
  levelLabelsByStep?: EvaluationLevelDisplayLabelsByStepCount | null;
  appliedPlanIds?: Set<string>;
  selectedPlanIds?: Set<string>;
  onToggleSelect?: (planId: string) => void;
  onFilterQueryChange: (value: string) => void;
  onViewAll: () => void;
  onAddPlan: () => void;
  onEditPlan: (plan: EvaluationPlanDraft) => void;
  onDeletePlan: (planId: string) => void;
}> = ({
  standards,
  plans,
  allPlans,
  filterQuery,
  filterStandardId,
  readOnly = false,
  allowAddPlan = true,
  editablePlanIds,
  planScopeById,
  levelStepCount,
  levelDisplayLabels,
  levelLabelsByStep,
  appliedPlanIds,
  selectedPlanIds,
  onToggleSelect,
  onFilterQueryChange,
  onViewAll,
  onAddPlan,
  onEditPlan,
  onDeletePlan,
}) => {
  const canEditPlan = (planId: string) => {
    if (readOnly) return false;
    if (editablePlanIds) return editablePlanIds.has(planId);
    return true;
  };
  const showAddButton = !readOnly && allowAddPlan;
  const [previewTarget, setPreviewTarget] = useState<{
    slot: EvaluationPlanAttachmentSlot;
    attachment: NonNullable<ReturnType<typeof getEvaluationPlanAttachment>>;
  } | null>(null);
  const [webDocumentViewerTarget, setWebDocumentViewerTarget] = useState<{
    subjectNames?: string[];
    slot: EvaluationPlanAttachmentSlot;
    attachment: NonNullable<ReturnType<typeof getEvaluationPlanAttachment>>;
    levelStepCount: EvaluationLevelStepCount;
    levelDisplayLabels: EvaluationLevelDisplayLabels | null;
  } | null>(null);
  const [sheetCanvasViewerTarget, setSheetCanvasViewerTarget] = useState<{
    subjectNames?: string[];
    attachment: NonNullable<ReturnType<typeof getEvaluationPlanAttachment>>;
  } | null>(null);

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => comparePlansByLinkedStandards(a, b, standards)),
    [plans, standards]
  );

  const filteredPlans = useMemo(
    () => sortedPlans.filter((plan) => planMatchesFilter(plan, filterQuery, standards)),
    [sortedPlans, filterQuery, standards]
  );

  const resolvePlanLevelMeta = (plan: EvaluationPlanDraft) => {
    const step = clampLevelStepCount(plan.level_step_count ?? levelStepCount ?? DEFAULT_LEVEL_STEP_COUNT);
    const labels =
      (levelLabelsByStep
        ? getLevelDisplayLabelsForStep(levelLabelsByStep, step)
        : levelDisplayLabels) ?? null;
    return { levelStepCount: step, levelDisplayLabels: labels };
  };

  const openAttachmentPreview = (plan: EvaluationPlanDraft, slot: EvaluationPlanAttachmentSlot) => {
    const attachment = getEvaluationPlanAttachment(plan, slot);
    if (!attachment || isShareLinkAttachment(attachment)) return;
    if (isWebDocumentAttachment(attachment)) {
      if (slot === 'sheet' && isSheetCanvasAttachment(attachment)) {
        setSheetCanvasViewerTarget({ attachment, subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
        return;
      }
      setWebDocumentViewerTarget({ slot, attachment, ...resolvePlanLevelMeta(plan), subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
      return;
    }
    setPreviewTarget({ slot, attachment });
  };

  const openWebDocumentViewer = (plan: EvaluationPlanDraft, slot: EvaluationPlanAttachmentSlot) => {
    const attachment = getEvaluationPlanAttachment(plan, slot);
    if (!attachment || !isWebDocumentAttachment(attachment)) return;
    if (slot === 'sheet' && isSheetCanvasAttachment(attachment)) {
      setSheetCanvasViewerTarget({ attachment, subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
      return;
    }
    setWebDocumentViewerTarget({ slot, attachment, ...resolvePlanLevelMeta(plan), subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
  };

  const renderAttachmentChipSlot = (plan: EvaluationPlanDraft, slot: EvaluationPlanAttachmentSlot) => {
    const label = EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot];
    const hasAttachment = hasEvaluationPlanSlotAttachment(plan, slot);
    if (!hasAttachment) {
      return (
        <span
          className={`cp-evaluation-plan-plan-attachment-chip-placeholder cp-evaluation-plan-plan-attachment-chip-placeholder--${slot}`}
          aria-hidden
        >
          {label}
        </span>
      );
    }
    const attachment = getEvaluationPlanAttachment(plan, slot);
    return (
      <EvaluationPlanShareLinkActionPopover
        attachment={attachment}
        onOpenDocument={
          attachment && isWebDocumentAttachment(attachment) ? () => openWebDocumentViewer(plan, slot) : undefined
        }
        onOpenPreview={
          attachment && !isShareLinkAttachment(attachment) && !isWebDocumentAttachment(attachment)
            ? () => openAttachmentPreview(plan, slot)
            : undefined
        }
        className="cp-evaluation-plan-plan-attachment-chip-popover-anchor"
      >
        <button type="button" className="cp-evaluation-plan-plan-attachment-chip">
          {label}
        </button>
      </EvaluationPlanShareLinkActionPopover>
    );
  };

  return (
    <article
      className="cp-dashboard-block cp-evaluation-plan-plans-block"
      aria-labelledby="cp-evaluation-plan-plans-head"
      data-tutorial-ep="plans-block"
    >
      <h3 className="cp-dashboard-block__head cp-evaluation-plan-section-head" id="cp-evaluation-plan-plans-head">
        <span className="cp-evaluation-plan-section-head-title">평가계획 목록</span>
        <div className="cp-evaluation-plan-section-head-actions">
          {showAddButton ? (
            <button
              type="button"
              className="cp-evaluation-plan-section-head-add-btn"
              data-tutorial-ep="add-plan"
              onClick={onAddPlan}
              title="평가계획 추가"
              aria-label="평가계획 추가"
            >
              +
            </button>
          ) : null}
        </div>
      </h3>
      <div className="cp-dashboard-block__body cp-evaluation-plan-section-body cp-evaluation-plan-plans-body">
        <>
            <div className="cp-evaluation-plan-filter-bar">
              <label className="cp-evaluation-plan-filter-field cp-evaluation-plan-filter-field--search">
                <span className="cp-evaluation-plan-filter-label">검색</span>
                <input
                  type="search"
                  className="cp-evaluation-plan-filter-input"
                  placeholder="평가계획 검색"
                  value={filterQuery}
                  onChange={(e) => onFilterQueryChange(e.target.value)}
                  aria-label="평가계획 필터"
                />
              </label>
              <button
                type="button"
                className="cp-btn-secondary cp-evaluation-plan-filter-view-all-btn"
                onClick={onViewAll}
              >
                전체 보기
              </button>
            </div>
            {sortedPlans.length === 0 ? (
              <p className="cp-dashboard-placeholder" role="status">
                {allPlans.length === 0 ? '등록된 평가계획이 없습니다. + 버튼으로 추가해 주세요.' : '표시할 평가계획이 없습니다.'}
              </p>
            ) : filteredPlans.length === 0 ? (
              <p className="cp-dashboard-placeholder" role="status">
                {filterStandardId ? '선택한 성취기준에 연결된 평가계획이 없습니다.' : '검색 결과가 없습니다.'}
              </p>
            ) : (
              <ul className="cp-admin-notices-edu-list cp-evaluation-plan-section-list" role="list">
                {filteredPlans.map((plan) => {
                  const linkedStandards = getPlanLinkedStandards(plan, standards);
                  const planScope = planScopeById?.[plan.id] ?? 'user';
                  const editable = canEditPlan(plan.id);
                  const isApplied = appliedPlanIds?.has(plan.id) === true;
                  const isSelected = selectedPlanIds?.has(plan.id) === true;
                  const checkLocked = isApplied;
                  const checkChecked = isApplied || isSelected;
                  return (
                    <li key={plan.id} className="cp-admin-notices-edu-card" role="none">
                      <div className="cp-admin-notices-edu-card-inner">
                        <span
                          className={`cp-evaluation-plan-plan-scope-stripe cp-evaluation-plan-plan-scope-stripe--${planScope}`}
                          aria-hidden
                        />
                        {onToggleSelect ? (
                          <button
                            type="button"
                            className={`cp-evaluation-plan-apply-check${checkChecked ? ' is-checked' : ''}${checkLocked ? ' is-locked' : ''}`}
                            aria-label={
                              checkLocked
                                ? '적용 평가계획에 등록됨'
                                : isSelected
                                  ? '적용 선택 해제'
                                  : '적용할 평가계획 선택'
                            }
                            aria-pressed={checkChecked}
                            aria-disabled={checkLocked}
                            disabled={checkLocked}
                            title={
                              checkLocked
                                ? '적용됨 (적용 평가계획에서 삭제 시 해제)'
                                : isSelected
                                  ? '선택 해제'
                                  : '선택'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (checkLocked) return;
                              onToggleSelect(plan.id);
                            }}
                          />
                        ) : null}
                        <button type="button" className="cp-admin-notices-edu-card-main" onClick={() => onEditPlan(plan)}>
                          <div className="cp-admin-notices-edu-card-meta">
                            {linkedStandards.length > 0 ? (
                              linkedStandards.map((standard) => {
                                const highlight = filterStandardId === standard.id;
                                return (
                                  <span
                                    key={`${plan.id}-${standard.id}`}
                                    className={`cp-evaluation-plan-plan-linked-standard-line${highlight ? ' is-filter-active' : ''}`}
                                  >
                                    {formatStandardSummary(standard)}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="cp-evaluation-plan-plan-linked-standard-line">(연결 성취기준 없음)</span>
                            )}
                          </div>
                          <span className="cp-admin-notices-edu-card-title">{formatPlanListSummary(plan)}</span>
                        </button>
                        <div className="cp-admin-notices-edu-card-tools">
                          <div
                            className="cp-evaluation-plan-plan-attachment-chips"
                            role="group"
                            aria-label="등록된 문서"
                          >
                            <div className="cp-evaluation-plan-plan-attachment-chip-slot cp-evaluation-plan-plan-attachment-chip-slot--criterion">
                              {renderAttachmentChipSlot(plan, 'criterion')}
                            </div>
                            <div className="cp-evaluation-plan-plan-attachment-chip-slot cp-evaluation-plan-plan-attachment-chip-slot--sheet">
                              {renderAttachmentChipSlot(plan, 'sheet')}
                            </div>
                          </div>
                          {editable ? (
                            <button
                              type="button"
                              className="cp-admin-notices-icon-btn"
                              title="수정"
                              aria-label="평가계획 수정"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditPlan(plan);
                              }}
                            >
                              <img
                                src="/edit.svg"
                                alt=""
                                className="cp-admin-notices-tool-icon"
                                width={18}
                                height={18}
                                decoding="async"
                              />
                            </button>
                          ) : null}
                          {editable ? (
                            <EvaluationPlanDeleteHoldButton itemId={plan.id} onRemove={() => onDeletePlan(plan.id)} />
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
        </>
      </div>

      <EvaluationPlanAttachmentPreviewModal
        open={previewTarget != null}
        slot={previewTarget?.slot ?? null}
        attachment={previewTarget?.attachment ?? null}
        onClose={() => setPreviewTarget(null)}
      />

      <EvaluationPlanWebDocumentViewerModal
        open={webDocumentViewerTarget != null}
        slot={webDocumentViewerTarget?.slot ?? null}
        attachment={webDocumentViewerTarget?.attachment ?? null}
        levelStepCount={webDocumentViewerTarget?.levelStepCount ?? levelStepCount}
        levelDisplayLabels={webDocumentViewerTarget?.levelDisplayLabels ?? levelDisplayLabels}
        subjectNames={webDocumentViewerTarget?.subjectNames}
        onClose={() => setWebDocumentViewerTarget(null)}
      />

      <EvaluationPlanSheetCanvasViewerModal
        open={sheetCanvasViewerTarget != null}
        slot="sheet"
        attachment={sheetCanvasViewerTarget?.attachment ?? null}
        subjectNames={sheetCanvasViewerTarget?.subjectNames}
        onClose={() => setSheetCanvasViewerTarget(null)}
      />
    </article>
  );
};

export default EvaluationPlanListPanel;
